import React, { useEffect, useState, useMemo } from "react";
import { loadCashfree, getCashfreeMode } from "../../../Utils/cashfree";
import SubscriptionCard, {
  STATIC_PLANS,
} from "../../atoms/Dashboard/Subscription/SubscriptionCard";
import styled, { keyframes } from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../Redux/Hooks";
import { subscriptionsPlansAPI } from "../../../Redux/ApiCalls/Dashboard/SubscriptionAPI";
import { SubscriptionType } from "../../../Data/subscription.dto";
import GetDurationPopup from "../../atoms/Dashboard/Subscription/GetPlanDurationPopUp";
import {
  completePaymentAPI,
  createOrderAPI,
} from "../../../Redux/ApiCalls/Dashboard/PaymentAPI";
import { cancelPayment } from "../../../Redux/Slice/Dashboard/PaymentSlice";
import CurrentPlan from "../../atoms/Dashboard/Subscription/CurrentPlan";
import BuyStorageModal from "../../atoms/Dashboard/Subscription/BuyStorageModal";
import { getUserActivePlanAPI } from "../../../Redux/ApiCalls/Auth/user";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import { config, env } from "../../../config/config";
import { showErrorToast } from "../../atoms/Utlis/Toast";
import {
  Sparkles,
  HardDrive,
  Database,
  Archive,
  Loader2,
  CreditCard,
  ChevronDown,
  ArrowLeft,
  Cloud,
  Plus,
} from "lucide-react";
import { device } from "../../atoms/Utlis/media";
import { setIsPlanChangedFlag } from "../../../Redux/Slice/Auth/AuthSlice";

type PaymentProvider = "cashfree" | "razorpay";

const GRADIENT =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const extractOrderPayload = (rawOrder: any) => {
  if (rawOrder?.data?.data && typeof rawOrder.data.data === "object") {
    return rawOrder.data.data;
  }

  if (rawOrder?.data && typeof rawOrder.data === "object") {
    return rawOrder.data;
  }

  return rawOrder;
};

const resolvePaymentProvider = (providerData: any): PaymentProvider => {
  const paymentProvider = String(providerData?.payment_provider || "").toLowerCase();

  if (paymentProvider === "cashfree" || providerData?.payment_session_id) {
    return "cashfree";
  }

  return "razorpay";
};

const SubscriptionPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { subscriptions, loading } = useAppSelector((state) => state.extra);
  const [activeCard, setActiveCard] = useState<number>();
  const [durationPopup, setDurationPopup] = useState(false);
  const [duration, setDuration] = useState<number>();

  const [currentCard, setCurrentCard] = useState<SubscriptionType>();
  const {
    order,
    paymentSuccess,
    loading: fetchOrderLoading,
  } = useAppSelector((state) => state.payment);
  const { user, currentActivePlan, isCurrentPlanChanged, storage } =
    useAppSelector((state) => state.auth);
  const { plan_discount } = useAppSelector((state) => state.flags);
  const [showCurrentPlan, setShowCurrentPlan] = useState<boolean>(false);
  const [buyStorageOpen, setBuyStorageOpen] = useState(false);

  const storageUsedPct = useMemo(() => {
    if (!storage?.total_storage) return 0;
    return Math.min(100, (storage.used_storage / storage.total_storage) * 100);
  }, [storage]);

  const extraStorageGb = Number(storage?.addon_storage ?? 0);
  const planStorageGb = Number(
    storage?.plan_storage ??
      Math.max(0, Number(storage?.total_storage ?? 0) - extraStorageGb)
  );

  useEffect(() => {
    sendFirebaseEvent(
      "Subscription",
      "Page_Preview",
      "Load_Subscription_Page",
      "Load_Subscription_Page"
    );
  }, []);

  useEffect(() => {
    if (!subscriptions) {
      dispatch(subscriptionsPlansAPI());
    } else {
      subscriptions.forEach((sub: SubscriptionType) => {
        if (sub.description === "MOST POPULAR") {
          setActiveCard(sub.id);
          setCurrentCard(sub);
        }
      });
    }

    return () => { };
  }, [subscriptions, dispatch]);

  useEffect(() => {
    const providerData = extractOrderPayload(order);

    if (!providerData || paymentSuccess || !currentCard || !duration) {
      return;
    }

    setDurationPopup(false);

    const openCheckout = async () => {
      const paymentProvider = resolvePaymentProvider(providerData);

      if (paymentProvider === "cashfree") {
        try {
          if (!providerData?.payment_session_id) {
            throw new Error("Cashfree session id is missing");
          }

          const mode = getCashfreeMode();
          const cashfree = await loadCashfree(mode);

          const checkoutResult = await cashfree.checkout({
            paymentSessionId: providerData.payment_session_id,
            redirectTarget: "_modal",
          });

          if (checkoutResult?.error) {
            throw new Error(
              checkoutResult.error?.message || "Cashfree payment failed"
            );
          }

          const paymentStatus =
            checkoutResult?.paymentDetails?.paymentStatus ||
            checkoutResult?.paymentDetails?.payment_status;

          if (paymentStatus && paymentStatus !== "SUCCESS") {
            throw new Error("Cashfree payment not completed");
          }

          sendFirebaseEvent(
            "Subscription",
            "User_Interaction",
            "Payment_Completed",
            `plan_id : ${currentCard.id}`
          );

          await dispatch(
            completePaymentAPI({
              settlement_for: "plan",
              payment_provider: "cashfree",
              order_id:
                providerData.order_id || providerData.cf_order_id || providerData.id,
              plan_id: currentCard.id,
              yearly_plan: duration === 12,
              plan_discount: Number(plan_discount || 0),
              amount:
                duration === 12
                  ? currentCard.storage_price_per_year
                  : currentCard.offer_price,
            })
          ).unwrap();

          return;
        } catch (error: any) {
          dispatch(cancelPayment());
          showErrorToast(error?.message || "Cashfree payment failed");
          return;
        }
      }

      if (!providerData?.id) {
        dispatch(cancelPayment());
        showErrorToast("Razorpay order id is missing");
        return;
      }

      const options = {
        key: config[env].RAZORPAY_KEY,
        currency: providerData.currency || "INR",
        name: "Kwik Media",
        description: `${currentCard.description}`,
        order_id: providerData.id,
        handler: async function (response: any) {
          sendFirebaseEvent(
            "Subscription",
            "User_Interaction",
            "Payment_Completed",
            `plan_id : ${currentCard.id}`
          );

          dispatch(
            completePaymentAPI({
              plan_id: currentCard.id,
              yearly_plan: duration === 12 ? "yearly_plan" : null,
              amount:
                duration === 12
                  ? currentCard.storage_price_per_year
                  : currentCard.offer_price,
              payment_provider: "razorpay",
              payment_id: response.razorpay_payment_id,
              order_id:
                response.razorpay_order_id ||
                response.razorpay_subscription_id ||
                providerData.id,
              signature: response.razorpay_signature,
              settlement_for: "plan",
            })
          );
        },
        prefill: {
          name: `${user?.name}`,
          email: `${user?.email}`,
          contact: `${user?.phone_number}`,
        },
        notes: {
          address: " Kwik Media Address.",
        },
        theme: {
          color: "#AE2AB1",
        },
      };

      const rzp = new (window as any).Razorpay(options);

      rzp.on("payment.failed", (response: any) => {
        console.log("payment failed", response);
        dispatch(cancelPayment());
      });

      rzp.on("payment.cancelled", () => {
        alert("Payment cancelled");
        dispatch(cancelPayment());
      });

      rzp.open();
    };

    openCheckout();
  }, [order, paymentSuccess, currentCard, duration, dispatch, user, plan_discount]);

  useEffect(() => {
    if (isCurrentPlanChanged) {
      dispatch(getUserActivePlanAPI());
    }
    if (!isCurrentPlanChanged && currentActivePlan) {
      setShowCurrentPlan(true);
    }
  }, [isCurrentPlanChanged, currentActivePlan, dispatch]);

  const handleActiveCard = (id: number) => {
    if (subscriptions) {
      subscriptions.forEach((sub: SubscriptionType) => {
        if (sub.id === id) {
          setActiveCard(sub.id);
          setCurrentCard(sub);
        }
      });
    }
  };

  const startPayment = (id: number, duration: number) => {
    setDuration(duration);
    const payload: {
      storage_plan_id: number;
      duration: number;
      settlement_for: string;
      plan_discount: string;
      yearly_plan?: "yearly_plan";
      payment_provider?: PaymentProvider;
      return_url?: string;
    } = {
      storage_plan_id: id,
      duration,
      settlement_for: "plan",
      plan_discount: plan_discount || "0",
      payment_provider: "cashfree",
    };

    if (duration === 12) {
      payload.yearly_plan = "yearly_plan";
    }

    dispatch(createOrderAPI(payload));
    sendFirebaseEvent(
      "Subscription",
      "User_Interaction",
      "Start_Payment",
      `plan_id : ${id}`
    );
  };

  const filteredPlans = useMemo(() => {
    if (!subscriptions) return [];
    return [...subscriptions]
      .filter((subscription) =>
        STATIC_PLANS.some(
          (staticPlan) =>
            staticPlan.name?.toLowerCase() ===
            String(subscription.description ?? "").toLowerCase()
        )
      )
      .sort((a, b) => (a.offer_price || 0) - (b.offer_price || 0));
  }, [subscriptions]);

  const isExplorePlans = Boolean(currentActivePlan && !showCurrentPlan);
  const isPlansFocus = !(showCurrentPlan && currentActivePlan);
  const useFixedPlansPanel = isExplorePlans;

  const handleBackToPlan = () => {
    setShowCurrentPlan(true);
  };

  return (
    <Page $compact={isExplorePlans} $fixed={useFixedPlansPanel}>
      {durationPopup && currentCard && (
        <GetDurationPopup
          loading={fetchOrderLoading}
          cancel={() => setDurationPopup(false)}
          Buy={(id, duration) => {
            startPayment(id, duration);
          }}
          card={currentCard}
        />
      )}

      {buyStorageOpen && (
        <BuyStorageModal
          onClose={() => setBuyStorageOpen(false)}
          onSuccess={() => {
            dispatch(setIsPlanChangedFlag(true));
            dispatch(getUserActivePlanAPI());
          }}
        />
      )}

      {isExplorePlans ? (
        <ExploreHeader>
          <BackBtn type="button" onClick={handleBackToPlan} aria-label="Back to your plan">
            <ArrowLeft size={18} />
            Back to your plan
          </BackBtn>
        </ExploreHeader>
      ) : (
        <Hero>
          <HeroGlow />
          <HeroContent>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={11} />
                Subscription
              </HeroBadge>
              <PageTitle className="linear-text">Plans & Pricing</PageTitle>
              <PageSubtitle>
                Manage your storage and choose a plan that scales with your studio.
              </PageSubtitle>
            </div>
          </HeroContent>
        </Hero>
      )}

      <ContentWrap $fixed={useFixedPlansPanel}>
        {storage && !isExplorePlans && (
          <StorageShowcase>
            <StorageShowcaseGlow />
            <StorageShowcaseInner>
              <StorageShowcaseHeader>
                <StorageShowcaseHeaderText>
                  <StorageShowcaseBadge>
                    <Cloud size={12} />
                    Cloud storage
                  </StorageShowcaseBadge>
                  <StorageShowcaseTitle>Your studio storage</StorageShowcaseTitle>
                  <StorageShowcaseSubtitle>
                    {storage.used_storage} GB of {storage.total_storage} GB in use
                    {extraStorageGb > 0
                      ? ` · includes ${extraStorageGb} GB extra storage`
                      : ""}
                  </StorageShowcaseSubtitle>
                </StorageShowcaseHeaderText>
                <BuyStorageBtn
                  type="button"
                  onClick={() => setBuyStorageOpen(true)}
                >
                  <Plus size={15} />
                  Storage
                </BuyStorageBtn>
              </StorageShowcaseHeader>

              <StorageShowcaseBody>
                <StorageRingWrap>
                  <StorageRingSvg viewBox="0 0 128 128" aria-hidden>
                    <defs>
                      <linearGradient id="storageRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFC951" />
                        <stop offset="100%" stopColor="#C94DAA" />
                      </linearGradient>
                    </defs>
                    <StorageRingTrack cx="64" cy="64" r="54" />
                    <StorageRingProgress
                      cx="64"
                      cy="64"
                      r="54"
                      $pct={storageUsedPct}
                    />
                  </StorageRingSvg>
                  <StorageRingCenter>
                    <StorageRingPct>{storageUsedPct.toFixed(0)}%</StorageRingPct>
                    <StorageRingLabel>Used</StorageRingLabel>
                  </StorageRingCenter>
                </StorageRingWrap>

                <StorageBreakdown>
                  <StorageBreakdownItem>
                    <StorageBreakdownIcon $tone="#c94daa">
                      <Database size={18} />
                    </StorageBreakdownIcon>
                    <StorageBreakdownText>
                      <StorageBreakdownValue>{storage.total_storage} GB</StorageBreakdownValue>
                      <StorageBreakdownLabel>Total capacity</StorageBreakdownLabel>
                    </StorageBreakdownText>
                  </StorageBreakdownItem>

                  <StorageBreakdownItem>
                    <StorageBreakdownIcon $tone="#7c5cbf">
                      <Cloud size={18} />
                    </StorageBreakdownIcon>
                    <StorageBreakdownText>
                      <StorageBreakdownValue>{planStorageGb} GB</StorageBreakdownValue>
                      <StorageBreakdownLabel>Plan storage</StorageBreakdownLabel>
                    </StorageBreakdownText>
                  </StorageBreakdownItem>

                  <StorageBreakdownItem>
                    <StorageBreakdownIcon $tone="#FFC951">
                      <Plus size={18} />
                    </StorageBreakdownIcon>
                    <StorageBreakdownText>
                      <StorageBreakdownValue>{extraStorageGb} GB</StorageBreakdownValue>
                      <StorageBreakdownLabel>Extra storage</StorageBreakdownLabel>
                    </StorageBreakdownText>
                  </StorageBreakdownItem>

                  <StorageBreakdownItem>
                    <StorageBreakdownIcon $tone="#e44862">
                      <Archive size={18} />
                    </StorageBreakdownIcon>
                    <StorageBreakdownText>
                      <StorageBreakdownValue>{storage.used_storage} GB</StorageBreakdownValue>
                      <StorageBreakdownLabel>Currently used</StorageBreakdownLabel>
                    </StorageBreakdownText>
                  </StorageBreakdownItem>

                  <StorageBreakdownItem>
                    <StorageBreakdownIcon $tone="#31a66a">
                      <HardDrive size={18} />
                    </StorageBreakdownIcon>
                    <StorageBreakdownText>
                      <StorageBreakdownValue>{storage.remaining_storage} GB</StorageBreakdownValue>
                      <StorageBreakdownLabel>Still available</StorageBreakdownLabel>
                    </StorageBreakdownText>
                  </StorageBreakdownItem>
                </StorageBreakdown>
              </StorageShowcaseBody>

              <StorageMeterFooter>
                <StorageMeterTrack>
                  <StorageMeterFill $pct={storageUsedPct} />
                </StorageMeterTrack>
                <StorageMeterLabels>
                  <span>{storage.used_storage} GB used</span>
                  <span>{storage.remaining_storage} GB free</span>
                </StorageMeterLabels>
              </StorageMeterFooter>
            </StorageShowcaseInner>
          </StorageShowcase>
        )}

        <PlansPanel $fill={useFixedPlansPanel} $highlighted={isPlansFocus}>
          <PanelHeader $highlighted={isPlansFocus}>
            <PanelTitleRow>
              <CreditCard size={16} color="#c94daa" />
              <PanelTitle $large={isPlansFocus}>
                {showCurrentPlan && currentActivePlan ? "Your plan" : "Choose a plan"}
              </PanelTitle>
            </PanelTitleRow>
            <PanelHint>
              {showCurrentPlan && currentActivePlan
                ? "Review your active subscription details"
                : isExplorePlans
                  ? "Compare plans and pick the best fit for your studio"
                  : "Whether your needs are large or small, we're here to help you scale."}
            </PanelHint>
          </PanelHeader>

          <PlanBody $scrollable={useFixedPlansPanel} $explore={isPlansFocus}>
            {showCurrentPlan && currentActivePlan ? (
              <CurrentPlanWrap>
                <CurrentPlan plan={currentActivePlan} />
              </CurrentPlanWrap>
            ) : loading ? (
              <LoadingWrap>
                <Spinner size={28} color="#c94daa" />
                Loading plans…
              </LoadingWrap>
            ) : filteredPlans.length > 0 ? (
              <PlansShowcase>
                <PlansShowcaseInner>
                  <ExploreBanner>
                    <ExploreBannerGlow />
                    <ExploreBannerContent>
                      <ExploreBadge>
                        <Sparkles size={12} />
                        Upgrade your studio
                      </ExploreBadge>
                      <ExploreTitle>Select a plan that fits your workflow</ExploreTitle>
                      <ExploreSubtitle>
                        All plans include cloud storage, team access, and studio tools.
                      </ExploreSubtitle>
                    </ExploreBannerContent>
                  </ExploreBanner>
                  <PlanGrid $highlighted>
                    {filteredPlans.map((subscription) => (
                      <SubscriptionCard
                        key={subscription.id}
                        card={subscription}
                        isActivated={activeCard === subscription.id}
                        onClick1={handleActiveCard}
                        setDurationPopup={setDurationPopup}
                      />
                    ))}
                  </PlanGrid>
                </PlansShowcaseInner>
              </PlansShowcase>
            ) : (
              <EmptyState>No subscription plans available right now.</EmptyState>
            )}
          </PlanBody>

          {currentActivePlan && !isExplorePlans && (
            <ToggleRow>
              <ToggleBtn
                type="button"
                onClick={() => {
                  sendFirebaseEvent(
                    "Subscription",
                    "User_Interaction",
                    "Explore_Plan_Button_Clicked",
                    "Explore_Plan_Button_Clicked"
                  );
                  setShowCurrentPlan(false);
                }}
              >
                Explore more plans
                <ChevronDown size={16} />
              </ToggleBtn>
            </ToggleRow>
          )}
        </PlansPanel>
      </ContentWrap>
    </Page>
  );
};

export default SubscriptionPage;

const Page = styled.div<{ $compact?: boolean; $fixed?: boolean }>`
  width: 100%;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$compact ? "12px" : "20px")};
  padding: ${(p) => (p.$compact ? "16px" : "24px")};
  font-family: Urbanist, sans-serif;
  background:
    radial-gradient(ellipse 70% 40% at 0% 0%, rgba(201, 77, 170, 0.05), transparent 55%),
    radial-gradient(ellipse 50% 35% at 100% 0%, rgba(255, 201, 81, 0.06), transparent 50%),
    transparent;

  ${(p) =>
    p.$fixed
      ? `
    overflow: hidden;
  `
      : `
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  `}

  @media ${device.tablet} {
    padding: ${(p) => (p.$compact ? "12px" : "16px")};
    gap: ${(p) => (p.$compact ? "10px" : "14px")};
  }
`;

const ExploreHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(23, 23, 23, 0.05);
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
    transform: translateX(-2px);
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const ContentWrap = styled.div<{ $fixed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: ${(p) => (p.$fixed ? "1" : "none")};
  min-height: ${(p) => (p.$fixed ? "0" : "auto")};
  overflow: ${(p) => (p.$fixed ? "hidden" : "visible")};
`;

const StorageShowcase = styled.div`
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(201, 77, 170, 0.18);
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.2) 0%,
    rgba(206, 86, 176, 0.16) 48%,
    rgba(53, 183, 200, 0.1) 100%
  );
  box-shadow: 0 16px 40px rgba(201, 77, 170, 0.14);
`;

const StorageShowcaseGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -60px;
  right: -40px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.42) 0%, transparent 70%);
`;

const StorageShowcaseInner = styled.div`
  position: relative;
  z-index: 1;
  padding: 22px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const StorageShowcaseHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const StorageShowcaseHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const BuyStorageBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 12px;
  background: ${GRADIENT};
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease;
  &:hover {
    transform: translateY(-1px);
  }
`;

const StorageShowcaseBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(201, 77, 170, 0.16);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StorageShowcaseTitle = styled.h2`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  line-height: 1.2;
`;

const StorageShowcaseSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
`;

const StorageShowcaseBody = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
  padding: 18px 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);

  @media ${device.laptop} {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
`;

const StorageRingWrap = styled.div`
  position: relative;
  width: 128px;
  height: 128px;
  flex-shrink: 0;
`;

const StorageRingSvg = styled.svg`
  width: 128px;
  height: 128px;
  transform: rotate(-90deg);
`;

const StorageRingTrack = styled.circle`
  fill: none;
  stroke: rgba(23, 23, 23, 0.08);
  stroke-width: 10;
`;

const StorageRingProgress = styled.circle<{ $pct: number }>`
  fill: none;
  stroke: url(#storageRingGradient);
  stroke-width: 10;
  stroke-linecap: round;
  stroke-dasharray: ${2 * Math.PI * 54};
  stroke-dashoffset: ${(p) => 2 * Math.PI * 54 * (1 - p.$pct / 100)};
  transition: stroke-dashoffset 0.5s ease;
`;

const StorageRingCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const StorageRingPct = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #171717;
  line-height: 1;
`;

const StorageRingLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const StorageBreakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
`;

const StorageBreakdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 14px rgba(23, 23, 23, 0.04);
  flex: 1 1 160px;
  min-width: 140px;
`;

const StorageBreakdownIcon = styled.div<{ $tone: string }>`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}14`};
`;

const StorageBreakdownText = styled.div`
  min-width: 0;
  text-align: left;
`;

const StorageBreakdownValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StorageBreakdownLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.48);
  margin-top: 2px;
`;

const StorageMeterFooter = styled.div`
  padding: 0 2px;
`;

const StorageMeterTrack = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.55);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.7);
`;

const StorageMeterFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${(p) => p.$pct}%;
  border-radius: 999px;
  background: ${GRADIENT};
  transition: width 0.4s ease;
`;

const StorageMeterLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.55);
`;

const PlansPanel = styled.div<{ $fill?: boolean; $highlighted?: boolean }>`
  border-radius: 20px;
  background: #fff;
  border: 1px solid
    ${(p) => (p.$highlighted ? "rgba(201, 77, 170, 0.22)" : "rgba(23, 23, 23, 0.06)")};
  box-shadow: ${(p) =>
    p.$highlighted
      ? "0 16px 40px rgba(201, 77, 170, 0.14)"
      : "0 8px 24px rgba(23, 23, 23, 0.04)"};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.$fill
      ? `
    flex: 1;
    min-height: 0;
  `
      : ""}
`;

const PanelHeader = styled.div<{ $highlighted?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
  background: ${(p) =>
    p.$highlighted
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.1) 0%, rgba(206, 86, 176, 0.08) 100%)"
      : "#fff"};
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelTitle = styled.h2<{ $large?: boolean }>`
  margin: 0;
  font-size: ${(p) => (p.$large ? "18px" : "16px")};
  font-weight: 800;
  color: #171717;
`;

const PanelHint = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.45);
`;

const PlanBody = styled.div<{ $scrollable?: boolean; $explore?: boolean }>`
  padding: ${(p) => (p.$explore ? "0" : "22px")};
  ${(p) =>
    p.$scrollable
      ? `
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  `
      : ""}
`;

const PlansShowcase = styled.div`
  min-height: 100%;
  background: linear-gradient(
    165deg,
    rgba(255, 201, 81, 0.14) 0%,
    rgba(206, 86, 176, 0.12) 42%,
    rgba(255, 255, 255, 0.85) 100%
  );
`;

const PlansShowcaseInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 24px 22px 36px;
  min-height: min-content;
`;

const ExploreBanner = styled.div`
  position: relative;
  width: 100%;
  max-width: 720px;
  overflow: hidden;
  border-radius: 18px;
  padding: 20px 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.22) 0%,
    rgba(206, 86, 176, 0.18) 55%,
    rgba(53, 183, 200, 0.1) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.18);
  box-shadow: 0 10px 28px rgba(201, 77, 170, 0.12);
  text-align: center;
`;

const ExploreBannerGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -40px;
  right: -20px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.35) 0%, transparent 70%);
`;

const ExploreBannerContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ExploreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(201, 77, 170, 0.16);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ExploreTitle = styled.h3`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.3;
`;

const ExploreSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
  max-width: 480px;
`;

const PlanGrid = styled.div<{ $highlighted?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => (p.$highlighted ? "24px" : "18px")};
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  padding: ${(p) => (p.$highlighted ? "8px 4px 16px" : "0 0 8px")};
`;

const CurrentPlanWrap = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: min-content;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  gap: 12px;
  color: rgba(23, 23, 23, 0.5);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 32px;
  border-radius: 18px;
  background: #fafafa;
  border: 1px dashed rgba(23, 23, 23, 0.1);
  color: rgba(23, 23, 23, 0.45);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 22px 22px;
  flex-shrink: 0;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  padding-top: 16px;
  background: #fff;
`;

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  border-radius: 12px;
  border: none;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: ${GRADIENT};
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.95;
  }
`;
